import { createContext, use, useId, type ReactNode } from 'react'
import { CircleAlert } from 'lucide-react'
import { cx } from './cx'

type FieldContextValue = {
  id: string
  describedBy: string | undefined
  invalid: boolean
  required: boolean
}

const FieldContext = createContext<FieldContextValue | null>(null)

type FieldProps = {
  label: ReactNode
  hint?: ReactNode
  /** Error text from validation or the server's 422 `fields`. Wins over the hint visually, both stay described. */
  error?: ReactNode
  required?: boolean
  /** Label sits visually hidden but stays the accessible name (e.g. table-inline editing). */
  hideLabel?: boolean
  /** Extra content at the right end of the label row, e.g. a "Passwort vergessen?" link. */
  labelAside?: ReactNode
  children: ReactNode
  className?: string
}

/** Label + control + hint/error. Wires htmlFor, aria-describedby and aria-invalid into the control inside. */
export function Field({ label, hint, error, required = false, hideLabel, labelAside, children, className }: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined
  return (
    <FieldContext value={{ id, describedBy, invalid: Boolean(error), required }}>
      <div className={cx('flex min-w-0 flex-col gap-1.5', className)}>
        <div className={cx('flex items-baseline justify-between gap-3', hideLabel && 'sr-only')}>
          <label htmlFor={id} className="text-small font-medium text-ink">
            {label}
            {required && (
              <span className="ml-0.5 text-ink-3" aria-hidden="true">
                *
              </span>
            )}
          </label>
          {labelAside && <span className="text-small">{labelAside}</span>}
        </div>
        {children}
        {error ? (
          <p id={errorId} className="flex items-start gap-1.5 text-small text-negative">
            <CircleAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" strokeWidth={2.25} />
            {error}
          </p>
        ) : null}
        {hint && (
          <p id={hintId} className={cx('text-small text-ink-3', Boolean(error) && 'sr-only')}>
            {hint}
          </p>
        )}
      </div>
    </FieldContext>
  )
}

type ControlAria = {
  id?: string | undefined
  'aria-describedby'?: string | undefined
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling' | undefined
  required?: boolean | undefined
}

/** Merge the surrounding Field's wiring into a control's own props (own props win). */
export function useFieldControl<P extends ControlAria>(props: P): P {
  const field = use(FieldContext)
  if (!field) return props
  return {
    ...props,
    id: props.id ?? field.id,
    'aria-describedby': [field.describedBy, props['aria-describedby']].filter(Boolean).join(' ') || undefined,
    'aria-invalid': props['aria-invalid'] ?? (field.invalid || undefined),
    required: props.required ?? (field.required || undefined),
  }
}
